import { TcpsProjectSettingsService } from './project-settings-service';
import { Client } from '@hey-api/client-fetch';
import { FormattingSettings, ProjectUnitSettingsService } from '@trimble/types';
import {
  mapFormatSettingsToUnitSettings,
  mapUnitSettingsToFormatSettings,
} from '../helpers/formatting-settings/formatting-settings-helper';

/**
 * Manages patching and retrieving unit settings for a project.
 * Utilizes TCPSProjectSettingsService to perform the underlying API requests.
 */
export class TCPSUnitSettingsService
  implements ProjectUnitSettingsService<FormattingSettings>
{
  private readonly requestService: TcpsProjectSettingsService;
  constructor(client: Client) {
    this.requestService = new TcpsProjectSettingsService(client);
  }

  /**
   * Update the unit settings for a specific project.
   * Uses the underlying request service to send the update.
   *
   * @param projectId - The ID of the project to update.
   * @param payload - The new unit settings to apply.
   * @returns The updated unit settings or null if not available.
   */
  public async updateUnitSettings(
    projectId: string,
    payload: FormattingSettings
  ): Promise<boolean | Error> {
    const unitSettings = mapFormatSettingsToUnitSettings(payload);
    return await this.requestService.updateProjectSettings(projectId, {
      unitSettings,
    });
  }

  /**
   * Retrieve the unit settings for a specific project.
   * Uses the underlying request service to fetch the settings.
   *
   * @param projectId - The ID of the project to retrieve settings for.
   * @returns The current unit settings or null if not set.
   */
  public async getUnitSettings(
    projectId: string
  ): Promise<FormattingSettings | null | Error> {
    return await this.requestService
      .getProjectSettings(projectId)
      .then((value) => {
        if (value instanceof Error) {
          return value;
        } else {
          return value.unitSettings
            ? mapUnitSettingsToFormatSettings(value.unitSettings)
            : null;
        }
      });
  }
}
